import { AppointmentEntity, ClientPackEntity } from '../store/entities/state';
import { InvoiceItem, InvoiceData, Payment, PaymentStatus } from '../models';

export const isCompletedInvoicePayment = (payment: Pick<Payment, 'status'>): boolean =>
    payment.status === PaymentStatus.Manual || payment.status === PaymentStatus.Success;

/**
 * Centralized invoice calculation utilities to ensure consistency across the application.
 * These functions handle discounts, VAT, and extras/expenses properly.
 */

export interface TaxRatesMap {
    [taxId: string]: { rate: number };
}

/**
 * Calculate the discount amount from invoice data (handles both percentage and fixed amount).
 * Discount is applied AFTER VAT.
 * @param invoiceData - The invoice data containing items and discount information
 * @param taxRatesMap - Map of tax IDs to tax rates
 * @returns The calculated discount amount (positive number)
 */
export const calculateInvoiceDiscount = (
    invoiceData: Pick<InvoiceData, 'items' | 'globalDiscountAmount' | 'globalDiscountPercentage'>,
    taxRatesMap: TaxRatesMap
): number => {
    // If there's a fixed discount amount, use it
    if (invoiceData.globalDiscountAmount !== undefined && invoiceData.globalDiscountAmount !== null) {
        return invoiceData.globalDiscountAmount;
    }

    // If there's a percentage discount, calculate it from the total WITH VAT
    if (invoiceData.globalDiscountPercentage !== undefined && invoiceData.globalDiscountPercentage !== null) {
        const totalWithVAT = calculateInvoiceItemsTotalWithVAT(invoiceData.items ?? [], taxRatesMap);
        return totalWithVAT * invoiceData.globalDiscountPercentage / 100;
    }

    // No discount
    return 0;
};

/**
 * Calculate the total value of an appointment including all expenses (extras and discounts).
 * This returns the value WITHOUT VAT.
 */
export const calculateAppointmentTotal = (appointment: AppointmentEntity): number => {
    const basePrice = appointment.price;
    const expensesTotal = (appointment.expenses ?? []).reduce(
        (sum, e) => sum + (e.value * (e.quantity ?? 1)),
        0
    );
    return basePrice + expensesTotal;
};

/**
 * Calculate the total value of a client pack including all extras and discounts.
 * This returns the value WITHOUT VAT.
 */
export const calculateClientPackTotal = (clientPack: ClientPackEntity): number => {
    const basePrice = clientPack.price;
    const extrasTotal = (clientPack.extras ?? []).reduce(
        (sum, e) => sum + (e.value * (e.quantity ?? 1)),
        0
    );
    return basePrice + extrasTotal;
};

/**
 * Calculate the total value of invoice items WITHOUT VAT.
 */
export const calculateInvoiceItemsTotalWithoutVAT = (items: InvoiceItem[]): number => {
    return items.reduce((sum, item) => {
        return sum + item.unitPrice * item.quantity;
    }, 0);
};

/**
 * Calculate the total value of invoice items WITH VAT.
 */
export const calculateInvoiceItemsTotalWithVAT = (
    items: InvoiceItem[],
    taxRatesMap: TaxRatesMap
): number => {
    return items.reduce((sum, item) => {
        const taxRate = taxRatesMap[item.taxId ?? '']?.rate ?? 0;
        return sum + item.unitPrice * item.quantity * (1 + taxRate / 100);
    }, 0);
};

/**
 * Calculate the discount amount from invoice items (items with negative unit price).
 * This returns the absolute value of the discount (positive number) WITHOUT VAT.
 */
export const calculateInvoiceDiscountWithoutVAT = (items: InvoiceItem[]): number => {
    return items
        .filter(item => item.unitPrice < 0)
        .reduce((sum, item) => {
            return sum + Math.abs(item.unitPrice * item.quantity);
        }, 0);
};

/**
 * Calculate the discount amount from invoice items (items with negative unit price).
 * This returns the absolute value of the discount (positive number) WITH VAT.
 */
export const calculateInvoiceDiscountWithVAT = (
    items: InvoiceItem[],
    taxRatesMap: TaxRatesMap
): number => {
    return items
        .filter(item => item.unitPrice < 0)
        .reduce((sum, item) => {
            const taxRate = taxRatesMap[item.taxId ?? '']?.rate ?? 0;
            return sum + Math.abs(item.unitPrice * item.quantity * (1 + taxRate / 100));
        }, 0);
};

/**
 * Calculate the total value of an appointment WITH VAT applied.
 * Requires the tax rate to be provided.
 */
export const calculateAppointmentTotalWithVAT = (
    appointment: AppointmentEntity,
    taxRate: number
): number => {
    const totalWithoutVAT = calculateAppointmentTotal(appointment);
    return totalWithoutVAT * (1 + taxRate / 100);
};

/**
 * Calculate the total value of a client pack WITH VAT applied.
 * Requires the tax rate to be provided.
 */
export const calculateClientPackTotalWithVAT = (
    clientPack: ClientPackEntity,
    taxRate: number
): number => {
    const totalWithoutVAT = calculateClientPackTotal(clientPack);
    return totalWithoutVAT * (1 + taxRate / 100);
};

/**
 * Calculate the final total of an invoice (with VAT and discount applied).
 * This is the amount the customer actually pays.
 * @param invoiceData - The invoice data containing items and discount information
 * @param taxRatesMap - Map of tax IDs to tax rates
 * @returns The final invoice total
 */
export const calculateInvoiceTotal = (
    invoiceData: Pick<InvoiceData, 'items' | 'globalDiscountAmount' | 'globalDiscountPercentage'>,
    taxRatesMap: TaxRatesMap
): number => {
    const totalWithVAT = calculateInvoiceItemsTotalWithVAT(invoiceData.items ?? [], taxRatesMap);
    const discount = calculateInvoiceDiscount(invoiceData, taxRatesMap);
    return totalWithVAT - discount;
};
